import { keys, has, reduce } from 'lodash';
import patch from './patch';
import CalculatorApi from '../../lib/calculator.api';
import selectors from '../selectors';
import { DEFAULT_INPUTS } from '../selectors/footprint';
import Logger from '../../Logger';
import debounce from '../../lib/debounce';
import * as userAPI from '../../lib/userApi';

const executeCompute = async (dispatch, params, operation) => {
  const response = await CalculatorApi.compute(params, operation);
  if (response.failed) {
    Logger.error(response);
    throw new Error(`API compute operation "${operation}" failed.`);
  }
  dispatch(patch({
    user_footprint: {
      [operation]: response,
    },
  }));
};

const notifyWidgetInputsUpdated = async (inputs, actionsTaken) => {
  if (!window.parentIFrame) return;
  const message = {
    type: 'inputs:update',
    data: {
      inputs,
      totalCt: keys(inputs).length,
      nonDefaultCt: keys(inputs).filter(key => !has(DEFAULT_INPUTS, key)).length,
      actionsTaken,
    },
  };
  window.parentIFrame.sendMessage(JSON.stringify(message));
};

const numericReducer = (previous, value, key) => ({
  ...previous,
  [key]: isNaN(value) ? value : parseFloat(value),
});

const makeUserPledgeData = (pledges, allSavings) => Object.keys(pledges)
  .reduce((previous, actionKey) => ({
    ...previous,
    [actionKey]: {
      status: pledges[actionKey],
      savingsAnnualDollars: allSavings.resultTakeActionDollars[actionKey],
      savingsAnnualCarbonTons: allSavings.resultTakeActionTons[actionKey],
      upfrontCost: allSavings.resultTakeActionNet10Yr[actionKey],
    },
  }), {});

const makeCalculatorAnswersPayload = (state) => {
  const { user_footprint: userFootprint, ui, pledges } = state;
  const inputs = keys(userFootprint.inputs).reduce((previous, key) => ({
    ...previous,
    [key]: has(DEFAULT_INPUTS, key) ?
      userFootprint.inputs[key].toString() : userFootprint.inputs[key],
  }), {});
  const selectedLocation = ui.selectedLocation;
  return {
    ui: {
      averageComparisonLocation: ui.averageComparisonLocation,
      unitSystem: ui.unitSystem,
      selectedLocation,
    },
    average_footprint: {
      get_defaults_and_results: state.average_footprint.get_defaults_and_results,
    },
    user_footprint: {
      inputs,
      electricVehicles: userFootprint.electricVehicles,
      compute_footprint: reduce(userFootprint.compute_footprint, numericReducer, {}),
      compute_takeaction_results:
        reduce(userFootprint.compute_takeaction_results, numericReducer, {}),
    },
    pledges: makeUserPledgeData(pledges, selectors.allSavings(state)),
  };
};

const updateUserInputs = (dispatch, getState, params, calculatorApiOp, updateServer = true) => {
  dispatch(patch({
    user_footprint: {
      inputs: params,
    },
  }));

  debounce(calculatorApiOp, async () => {
    const originalState = getState();

    notifyWidgetInputsUpdated(
      originalState.user_footprint.inputs,
      selectors.actionsTaken(originalState),
    );

    if (calculatorApiOp === 'compute_footprint') {
      const footprintParams = selectors.params.computeFootprint(originalState);
      await executeCompute(dispatch, footprintParams, 'compute_footprint');
      const takeactionParams = selectors.params.computeTakeActionResults(getState());
      await executeCompute(dispatch, takeactionParams, 'compute_takeaction_results');
    } else {
      const takeactionParams = selectors.params.computeTakeActionResults(getState());
      await executeCompute(dispatch, takeactionParams, 'compute_takeaction_results');
    }

    if (!updateServer) return;

    debounce('updateCalculatorAnswers', () => {
      const state = getState();
      if (selectors.isTokenValid(state)) {
        const { user, auth } = state;
        const token = auth.token;
        const payload = makeCalculatorAnswersPayload(state);
        // save these in background.
        userAPI.updateCalculatorAnswers(user, payload, token);
      }
    }, 4000);
  });
};

/**
 * computeUserFootprint will merge the new params
 * into state.user_footprint.inputs then use that merge to make
 * API call to Calculator API compute_footprint op. This will also call
 * "compute_takeaction_results" as necessary.
 * @param {Object.<String, String|Number>} params Any subset of input API keys
 * used in op=compute_footprint.
 */
export const computeUserFootprint = (params = {}, updateServer = true) => (dispatch, getState) =>
  updateUserInputs(dispatch, getState, params, 'compute_footprint', updateServer);

/**
 * computeTakeActionResults will merge the new params
 * into state.user_footprint.inputs then use that merge to make
 * API call to Calculator API compute_takeaction_results op.
 * @param {Object.<String, String|Number>} params Any subset of input API keys
 * used in op=compute_takeaction_results.
 */
export const computeTakeActionResults = (params = {}) => (dispatch, getState) =>
  updateUserInputs(dispatch, getState, params, 'compute_takeaction_results');
